<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import BarChart from 'shared/components/charts/BarChart.vue';
import Spinner from 'shared/components/Spinner.vue';
import MetaReportsAPI from 'dashboard/api/metaReports';
import {
  fmt,
  fmtPct,
  statusBadgeClass,
  qualityBadgeClass,
  statusLabel,
  qualityLabel,
} from '../helper/metaFormatters';

const props = defineProps({
  templateId: { type: String, required: true },
  inboxId: { type: [Number, String], default: null },
  since: { type: Number, required: true },
  until: { type: Number, required: true },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isLoading = ref(false);
const detail = ref(null);
const activeChart = ref('trend');

const goBack = () =>
  router.push({ name: 'meta_reports_templates', query: route.query });

const totals = computed(
  () => detail.value?.totals || { sent: 0, delivered: 0, read: 0, clicked: 0 }
);
const pctOfSent = value =>
  totals.value.sent > 0 ? (value / totals.value.sent) * 100 : 0;

const hasData = computed(() => (totals.value.sent || 0) > 0);

// ── Preview del mensaje a partir de los components ──
const components = computed(() => detail.value?.preview || []);
const findComponent = type => components.value.find(c => c.type === type);
const headerText = computed(() => {
  const header = findComponent('HEADER');
  return header && header.format === 'TEXT' ? header.text : '';
});
const headerMedia = computed(() => {
  const header = findComponent('HEADER');
  if (!header || !['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
    return null;
  }
  // El backend resuelve la URL real enviada; el example del catálogo queda de respaldo.
  const url =
    detail.value?.header_media?.url || header.example?.header_handle?.[0] || '';
  return { format: header.format, url };
});
const mediaFailed = ref(false);
watch(headerMedia, () => {
  mediaFailed.value = false;
});
const showMediaPreview = computed(
  () => !!headerMedia.value?.url && !mediaFailed.value
);
const bodyText = computed(() => findComponent('BODY')?.text || '');
const footerText = computed(() => findComponent('FOOTER')?.text || '');
const previewButtons = computed(() => findComponent('BUTTONS')?.buttons || []);

const buttons = computed(() => detail.value?.buttons || []);

const funnelColors = {
  sent: '#3b82f6',
  delivered: '#8b5cf6',
  read: '#10b981',
  clicked: '#f59e0b',
};
const funnelSteps = computed(() => [
  {
    key: 'sent',
    label: t('META_REPORTS.MESSAGES.SENT'),
    value: totals.value.sent,
    pct: 100,
  },
  {
    key: 'delivered',
    label: t('META_REPORTS.MESSAGES.DELIVERED'),
    value: totals.value.delivered,
    pct: pctOfSent(totals.value.delivered),
  },
  {
    key: 'read',
    label: t('META_REPORTS.TEMPLATES.READ'),
    value: totals.value.read,
    pct: pctOfSent(totals.value.read),
  },
  {
    key: 'clicked',
    label: t('META_REPORTS.TEMPLATES.CLICKED'),
    value: totals.value.clicked,
    pct: pctOfSent(totals.value.clicked),
  },
]);

const trendData = computed(() => {
  const points = detail.value?.data_points || [];
  const labels = points.map(p => format(fromUnixTime(p.start), 'dd/MM'));
  const line = (label, key, color) => ({
    type: 'line',
    label,
    data: points.map(p => p[key] || 0),
    borderColor: color,
    backgroundColor: color,
    fill: false,
    pointRadius: 0,
    tension: 0.3,
    borderWidth: 2,
  });
  return {
    labels,
    datasets: [
      line(t('META_REPORTS.MESSAGES.SENT'), 'sent', funnelColors.sent),
      line(t('META_REPORTS.MESSAGES.DELIVERED'), 'delivered', funnelColors.delivered),
      line(t('META_REPORTS.TEMPLATES.READ'), 'read', funnelColors.read),
      line(t('META_REPORTS.TEMPLATES.CLICKED'), 'clicked', funnelColors.clicked),
    ],
  };
});

const trendOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: { legend: { display: true, labels: { color: '#8b95a5' } } },
  scales: {
    x: {
      ticks: {
        color: '#8b95a5',
        autoSkip: true,
        maxTicksLimit: 16,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      ticks: { color: '#8b95a5', precision: 0, callback: value => fmt(value) },
    },
  },
}));

const fetchDetail = async () => {
  if (!props.inboxId || !props.templateId) return;
  isLoading.value = true;
  try {
    const { data } = await MetaReportsAPI.getTemplate({
      inboxId: props.inboxId,
      templateId: props.templateId,
      since: props.since,
      until: props.until,
    });
    detail.value = data;
  } catch (error) {
    detail.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.inboxId, props.templateId, props.since, props.until],
  fetchDetail,
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <button
      class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 w-fit"
      @click="goBack"
    >
      <span aria-hidden="true">←</span>
      {{ $t('META_REPORTS.TEMPLATE_DETAIL.BACK') }}
    </button>

    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner size="" />
    </div>

    <template v-else>
      <!-- Cabecera -->
      <div>
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ detail?.name || templateId }}
          <span
            v-if="detail?.language"
            class="text-base font-normal text-n-slate-11 uppercase"
          >
            · {{ detail.language }}
          </span>
        </h2>
        <div v-if="detail" class="flex items-center gap-2 mt-2 flex-wrap">
          <span v-if="detail.status" :class="statusBadgeClass(detail.status)">
            {{ statusLabel(detail.status, t) }}
          </span>
          <span v-if="detail.category" class="text-xs text-n-slate-11">
            {{ detail.category }}
          </span>
          <span :class="qualityBadgeClass(detail.quality_score)">
            {{ qualityLabel(detail.quality_score, t) }}
          </span>
        </div>
      </div>
      <!-- KPIs -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
        >
          <span class="text-sm text-n-slate-11">
            {{ $t('META_REPORTS.MESSAGES.SENT') }}
          </span>
          <p class="text-2xl font-semibold text-n-slate-12 mt-1 tabular-nums">
            {{ fmt(totals.sent) }}
          </p>
        </div>
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
        >
          <span class="text-sm text-n-slate-11">
            {{ $t('META_REPORTS.MESSAGES.DELIVERED') }}
          </span>
          <p class="text-2xl font-semibold text-n-slate-12 mt-1 tabular-nums">
            {{ fmt(totals.delivered) }}
            <span class="text-sm font-normal text-n-slate-11">
              ({{ fmtPct(pctOfSent(totals.delivered)) }})
            </span>
          </p>
        </div>
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
        >
          <span class="text-sm text-n-slate-11">
            {{ $t('META_REPORTS.TEMPLATES.READ') }}
          </span>
          <p class="text-2xl font-semibold text-n-slate-12 mt-1 tabular-nums">
            {{ fmt(totals.read) }}
            <span class="text-sm font-normal text-n-slate-11">
              ({{ fmtPct(pctOfSent(totals.read)) }})
            </span>
          </p>
        </div>
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
        >
          <span class="text-sm text-n-slate-11">
            {{ $t('META_REPORTS.TEMPLATES.CLICKED') }}
          </span>
          <p class="text-2xl font-semibold text-n-slate-12 mt-1 tabular-nums">
            {{ fmt(totals.clicked) }}
            <span class="text-sm font-normal text-n-slate-11">
              ({{ fmtPct(pctOfSent(totals.clicked)) }})
            </span>
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Preview del mensaje -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h3 class="text-sm font-medium text-n-slate-11 mb-3">
            {{ $t('META_REPORTS.TEMPLATE_DETAIL.PREVIEW') }}
          </h3>
          <div
            class="rounded-lg bg-[#efe7dd] dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700"
          >
            <div
              class="bg-white dark:bg-slate-900 rounded-lg shadow-sm px-3 py-2 text-sm text-n-slate-12 max-w-xs"
            >
              <template v-if="headerMedia">
                <img
                  v-if="showMediaPreview && headerMedia.format === 'IMAGE'"
                  :src="headerMedia.url"
                  :alt="headerMedia.format"
                  class="w-full max-h-48 object-cover mb-2 rounded bg-slate-100 dark:bg-slate-800"
                  @error="mediaFailed = true"
                />
                <video
                  v-else-if="showMediaPreview && headerMedia.format === 'VIDEO'"
                  :src="headerMedia.url"
                  controls
                  class="w-full max-h-48 mb-2 rounded bg-slate-100 dark:bg-slate-800"
                  @error="mediaFailed = true"
                />
                <a
                  v-else-if="showMediaPreview"
                  :href="headerMedia.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center justify-center h-24 mb-2 rounded bg-slate-100 dark:bg-slate-800 text-n-blue-text text-xs uppercase"
                >
                  {{ headerMedia.format }}
                </a>
                <div
                  v-else
                  class="flex items-center justify-center h-24 mb-2 rounded bg-slate-100 dark:bg-slate-800 text-n-slate-10 text-xs uppercase"
                >
                  {{ headerMedia.format }}
                </div>
              </template>
              <p v-if="headerText" class="font-semibold mb-1">
                {{ headerText }}
              </p>
              <p class="whitespace-pre-line">{{ bodyText }}</p>
              <p v-if="footerText" class="text-xs text-n-slate-10 mt-2">
                {{ footerText }}
              </p>
            </div>
            <div v-if="previewButtons.length" class="flex flex-col gap-1 mt-2">
              <div
                v-for="(btn, idx) in previewButtons"
                :key="idx"
                class="bg-white dark:bg-slate-900 rounded-lg text-center text-sm text-n-blue-text py-2 shadow-sm"
              >
                {{ btn.text }}
              </div>
            </div>
          </div>
        </div>

        <!-- Gráfico: tendencia / embudo -->
        <div
          class="lg:col-span-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <div class="flex items-center gap-2 mb-4">
            <button
              class="px-3 py-1 text-sm rounded-md"
              :class="
                activeChart === 'trend'
                  ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                  : 'text-n-slate-11'
              "
              @click="activeChart = 'trend'"
            >
              {{ $t('META_REPORTS.TEMPLATE_DETAIL.TREND') }}
            </button>
            <button
              class="px-3 py-1 text-sm rounded-md"
              :class="
                activeChart === 'funnel'
                  ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                  : 'text-n-slate-11'
              "
              @click="activeChart = 'funnel'"
            >
              {{ $t('META_REPORTS.TEMPLATE_DETAIL.FUNNEL') }}
            </button>
          </div>

          <div
            v-if="!hasData"
            class="flex items-center justify-center h-64 text-sm text-n-slate-11"
          >
            {{ $t('META_REPORTS.NO_DATA') }}
          </div>

          <div v-else-if="activeChart === 'trend'" class="h-72">
            <BarChart :collection="trendData" :chart-options="trendOptions" />
          </div>

          <div v-else class="flex flex-col gap-3 py-2">
            <div v-for="step in funnelSteps" :key="step.key">
              <div class="flex justify-between text-sm mb-1">
                <span class="text-n-slate-11">{{ step.label }}</span>
                <span class="text-n-slate-12 tabular-nums font-medium">
                  {{ fmt(step.value) }}
                  <span class="text-n-slate-10">({{ fmtPct(step.pct) }})</span>
                </span>
              </div>
              <div class="h-3 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div
                  class="h-full rounded"
                  :style="{
                    width: `${step.pct}%`,
                    backgroundColor: funnelColors[step.key],
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Clics por botón -->
      <div v-if="buttons.length">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ $t('META_REPORTS.TEMPLATE_DETAIL.BUTTON_CLICKS') }}
        </h3>
        <div class="border border-n-weak rounded-lg overflow-auto">
          <table class="w-full text-sm border-collapse">
            <thead
              class="bg-white dark:bg-slate-900 border-b border-n-weak"
            >
              <tr class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
                <th class="text-left py-2.5 px-4">
                  {{ $t('META_REPORTS.TEMPLATE_DETAIL.LABEL') }}
                </th>
                <th class="text-left py-2.5 px-3">
                  {{ $t('META_REPORTS.TEMPLATE_DETAIL.TYPE') }}
                </th>
                <th class="text-right py-2.5 px-3">
                  {{ $t('META_REPORTS.TEMPLATE_DETAIL.TOTAL_CLICKS') }}
                </th>
                <th class="text-right py-2.5 px-4">
                  {{ $t('META_REPORTS.TEMPLATE_DETAIL.CTR') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="btn in buttons"
                :key="btn.label"
                class="border-b border-n-weak last:border-0 text-n-slate-12"
              >
                <td class="py-2.5 px-4">{{ btn.label }}</td>
                <td class="py-2.5 px-3 text-n-slate-11">{{ btn.type || '—' }}</td>
                <td class="py-2.5 px-3 text-right tabular-nums">
                  {{ fmt(btn.count) }}
                </td>
                <td class="py-2.5 px-4 text-right tabular-nums">
                  {{ fmtPct(btn.ctr) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
